/**
 * Арифметика звукового напоминания (ТЗ 0068) — без React и без DOM, чтобы
 * каждое правило проверялось отдельно от хука, который его исполняет.
 *
 * Два решения, которые здесь важнее остального:
 *
 * 1. **Решение принимается по стенным часам, а не по тикам таймера.** В
 *    фоновой вкладке браузер троттлит `setInterval` до ~одного раза в минуту,
 *    а `requestAnimationFrame` не вызывает вовсе. Считай мы интервал тиками —
 *    сигнал то опаздывал бы вдвое, то приходил пачкой при возврате на вкладку.
 *    Сравнение `now - lastBeepAt >= repeatMs` даёт единственное отклонение,
 *    которое мы готовы принять: сигнал может ОПОЗДАТЬ, но не может ни
 *    продублироваться, ни отсчитать интервал короче положенного.
 *
 * 2. **Тихие часы через полночь — норма, а не частный случай.** «Не беспокоить
 *    с 22:00 до 08:00» это и есть типичная настройка, поэтому окно с
 *    переходом через сутки обрабатывается явно.
 */

/** Время «ЧЧ:ММ» или «ЧЧ:ММ:СС» — сервер присылает второе. */
export type THHMM = string;

/**
 * 🔴 Секунды принимаются, и это не мелочь.
 *
 * Скаляр `Time` в graphene сериализуется как `time.isoformat()`, то есть
 * «22:00:00». Регулярка, требовавшая ровно `ЧЧ:ММ`, не совпадала — `isQuietNow`
 * возвращал `false` ВСЕГДА, и тихие часы не работали вовсе. Тесты этого не
 * ловили, потому что подавали формат, которого сервер не присылает.
 */
const toMinutes = (value: THHMM): number | null => {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value ?? '');
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

/**
 * Идут ли сейчас тихие часы.
 *
 * Равные или незаданные границы означают «окна нет»: 08:00→08:00 читалось бы
 * то ли как «нисколько», то ли как «круглые сутки», и оба ответа плохи.
 */
export const isQuietNow = (
  nowHHMM: THHMM,
  from: THHMM | null | undefined,
  to: THHMM | null | undefined,
): boolean => {
  const start = from ? toMinutes(from) : null;
  const end = to ? toMinutes(to) : null;
  const now = toMinutes(nowHHMM);
  if (start === null || end === null || now === null || start === end) {
    return false;
  }
  if (start < end) return now >= start && now < end;
  // Окно через полночь: 22:00 → 08:00.
  return now >= start || now < end;
};

export interface IBeepInput {
  /** `Date.now()`. */
  now: number;
  /** Сервер сказал, что есть из-за чего звенеть. */
  alarmActive: boolean;
  /** Настройка сотрудника. */
  soundEnabled: boolean;
  /** Браузер уже разрешил звук в этой загрузке страницы. */
  unlocked: boolean;
  /** Панель уведомлений открыта — человек и так смотрит на них. */
  panelOpen: boolean;
  /** «Отложить на час»: метка времени в мс, 0 — не отложено. */
  mutedUntil: number;
  /** Когда звенели в последний раз (мс). 0 — ещё ни разу. */
  lastBeepAt: number;
  /** Интервал повтора в мс. */
  repeatMs: number;
  /** Локальное время «ЧЧ:ММ» — для тихих часов. */
  nowHHMM: THHMM;
  quietFrom?: THHMM | null;
  quietTo?: THHMM | null;
}

/**
 * Звенеть ли прямо сейчас.
 *
 * Порядок проверок не случаен: сначала то, что выключает звук полностью
 * (настройка, разрешение браузера, отсутствие повода), потом временны́е
 * условия. Так отладка отвечает на вопрос «почему молчит» первой же
 * неподошедшей строкой.
 */
export const shouldBeep = (input: IBeepInput): boolean => {
  if (!input.soundEnabled) return false;
  if (!input.alarmActive) return false;
  // Пока браузер не дал разрешения, попытка проиграть звук только забивает
  // консоль `NotAllowedError` — а сигнала всё равно нет. Молчим и говорим об
  // этом в интерфейсе.
  if (!input.unlocked) return false;
  // Панель открыта — человек уже смотрит на уведомления, звенеть незачем.
  if (input.panelOpen) return false;
  if (input.mutedUntil > input.now) return false;
  if (isQuietNow(input.nowHHMM, input.quietFrom, input.quietTo)) return false;
  // Первый раз — сразу; дальше не чаще, чем раз в `repeatMs`.
  if (!input.lastBeepAt) return true;
  return input.now - input.lastBeepAt >= input.repeatMs;
};

/** Локальное «ЧЧ:ММ» из отметки времени. */
export const toHHMM = (at: Date): THHMM =>
  `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(
    2,
    '0',
  )}`;

/**
 * Привести время к «ЧЧ:ММ» — в таком виде его показывают контролы.
 *
 * Сервер присылает «22:00:00», а опции селекта — «22:00»; без приведения antd
 * не находил совпадения и рисовал сырое значение, из-за чего сохранённая
 * настройка выглядела несохранённой.
 */
export const toSelectValue = (value?: string | null): string | undefined => {
  const minutes = toMinutes(value ?? '');
  if (minutes === null) return undefined;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};
